/**
 * The client for /api/quote-jobs (migration 120, docs/job/GOAL.md).
 *
 * Only the wire. The backend contract lives in backend/src/routes/quote-jobs.js.
 *
 * apiFetch camelises response keys at every level EXCEPT inside the opaque JSON
 * columns (quote_data, form_data, scope, ai_context, ...), whose keys are
 * camelised but whose values pass through byte-for-byte. So a project row
 * arrives as { quoteRev, qmQuoteNo, quoteJobId, quoteData: <as stored> }.
 *
 * NUMERIC columns (a frozen revision's totals) arrive as strings; moneyOrNull
 * turns them into numbers and never turns "absent" into 0.
 *
 * Errors: apiFetch throws with err.code set to the route's machine code
 * (PROJECT_STALE_WRITE, QUOTE_NUMBER_EXISTS, SPLIT_ITEM_UNMATCHED, ...) and
 * err.body carrying the full response, so callers can explain a refusal.
 */

import { apiFetch } from '../data-service-api.js';
import type { RevisionReason, RevisionReasonKind } from './revision-reason';
import type { RevisionAttachment } from './revision-attachments';

export interface QuoteJobsCapabilities {
    enabled: boolean;
    killSwitch: boolean;
}

export interface QuoteJob {
    id: string;
    title: string;
    quoteRequestNo: string | null;
    strataPlan: string | null;
    clientName: string | null;
    siteAddress: string | null;
    intakeJobId: string | null;
    origin: 'revise' | 'split' | 'import' | 'group';
    createdAt: string;
    updatedAt: string;
}

export interface QuoteJobMember {
    id: string;
    title: string;
    quoteNumber: string | null;
    qmQuoteNo: string | null;
    quoteRev: number;
    sourceQuoteRef: string | null;
    sourceIssuer: string | null;
    status: string | null;
    quoteStatus: string | null;
    pdfGenerated: boolean;
    emailSent: boolean;
    emailSentAt: string | null;
    currentStage: number | null;
    quoteTotal: number | null;
    createdAt: string;
    updatedAt: string;
}

export type QuoteRevisionReason = 'sent' | 'revised' | 'split_source' | 'imported';

export interface QuoteRevisionSummary {
    id: string;
    projectId: string | null;
    quoteJobId: string | null;
    quoteRev: number;
    qmQuoteNo: string | null;
    sourceQuoteRef: string | null;
    /** The Quote # exactly as it printed when frozen, e.g. 'RBB260605 Rev 1'. */
    displayQuoteNo: string;
    title: string | null;
    subtotalExGst: number | null;
    gstAmount: number | null;
    totalIncGst: number | null;
    gstDisplayMode: string | null;
    contentSha256: string;
    frozenReason: QuoteRevisionReason;
    sentAt: string | null;
    createdAt: string;
    /** The imported document's own printed total inc GST. Only on an 'imported' copy whose document stated one. */
    documentTotalIncGst: number | null;
}

export interface QuoteRevision extends QuoteRevisionSummary {
    quoteData: Record<string, unknown>;
    formData: Record<string, unknown>;
    scope: unknown[];
}

export interface QuoteJobView {
    job: QuoteJob;
    quotes: QuoteJobMember[];
    revisions: QuoteRevisionSummary[];
}

export interface SkippedMediaReference {
    fileMetadataId?: string;
    s3Key?: string;
    reason: 'missing' | 'other-aggregate' | 'no-durable-receipt';
}

export interface SplitDerived {
    /** scopeFingerprintForQuoteAuthoring of that half's Stage 4 scope. */
    sourceAuthoringFingerprint?: string;
    /** That half's Objective, derived from its own items (services/quote-jobs/split-copy.ts). */
    scopeObjective?: string;
    subtotalExGst?: number;
    gstAmount?: number;
    totalIncGst?: number;
}

export interface SplitRequest {
    expectedUpdatedAt: string;
    targetProjectId: string;
    itemIds: string[];
    areaName: string;
    derived?: { source?: SplitDerived; target?: SplitDerived };
    /** Flag the Objective and/or the Note for checking on both quotes. */
    review?: { objective: boolean; note: boolean };
    /**
     * Why a split of an ISSUED quote makes a new revision of what stays
     * (JOBS-UX-SPEC 3.2). The server refuses a reason on a split of a quote
     * that was never issued (REVISION_REASON_NOT_ALLOWED).
     */
    reason?: RevisionReason | null;
}

/**
 * The history row a Revise or Split wrote (quote_job_events, migration 122,
 * JOBS-UX-SPEC 6.1). reasonKind is null when the request carried no reason.
 */
export interface QuoteJobReasonEvent {
    id: string;
    kind: string;
    quoteRev: number | null;
    reasonKind: RevisionReasonKind | null;
    note: string | null;
    siteVisitNeeded: boolean | null;
    occurredAt: string | null;
}

/** A reference document the server wrote for a reason: the reason file and each text attachment. */
export interface ReasonDocumentRef {
    id: string;
    name: string;
}

/** What a Revise request carries beyond the save token (JOBS-UX-SPEC 6.1). */
export interface ReviseRequestExtras {
    reason?: RevisionReason | null;
    /** Emails and text files, read in the browser. Only with a reason. */
    attachments?: RevisionAttachment[];
    /** PDFs and photos added after the reload, named so the reason file can list them. */
    otherFileNames?: string[];
}

export interface SplitResult {
    /** The original quote after the split, with camelCase column names like target (see sourceRowColumnsToCamel). */
    source: Record<string, any> | null;
    target: Record<string, any>;
    job: QuoteJob | null;
    skippedMedia: SkippedMediaReference[];
    /** 'QM_MONTHLY_OVERFLOW' when the month had no Quote # left for the new quote. */
    quoteNumberWarning: string | null;
    replayed?: boolean;
    /** True when the original quote had been issued, so what stays moved to its next revision, unsent. */
    sourceAdvanced: boolean;
    /** The split event, with the operator's reason when one was sent. Null from a backend before migration 122. */
    reasonEvent: QuoteJobReasonEvent | null;
    reasonDocuments: ReasonDocumentRef[];
}

export interface ReviseResult {
    project: Record<string, any>;
    job: QuoteJob;
    frozenRevision: QuoteRevisionSummary | null;
    revisions: QuoteRevisionSummary[];
    /** The revised event. Null from a backend before migration 122. */
    reasonEvent: QuoteJobReasonEvent | null;
    reasonDocuments: ReasonDocumentRef[];
}

export type ImportQuoteNumber =
    | { kind: 'tenant'; qmQuoteNo: string; quoteRev: number }
    | { kind: 'external'; sourceQuoteRef: string; sourceIssuer: string | null; quoteRev: number }
    | { kind: 'none'; quoteRev?: number };

export interface ImportQuoteRequest {
    projectId: string;
    title: string;
    siteAddress: string | null;
    quoteRequestNo: string | null;
    isGstInclusive: boolean;
    number: ImportQuoteNumber;
    /** ISO date when the quote was already sent to the client, else null. */
    sentAt: string | null;
    sourceFile: { name: string; kind: 'pdf' | 'docx' | 'doc' | 'rtf'; sha256: string | null; byteLength: number };
    /** The editor-shaped quote. scope items need id and title; images must be file references, never data URLs. */
    quoteData: Record<string, unknown>;
    formData: Record<string, unknown>;
}

export interface ImportQuoteResult {
    project: Record<string, any>;
    job: QuoteJob;
    frozenRevision: QuoteRevisionSummary | null;
}

const BASE = '/quote-jobs';

function moneyOrNull(value: unknown): number | null {
    if (value === null || value === undefined || value === '') return null;
    const number = typeof value === 'number' ? value : Number(String(value));
    return Number.isFinite(number) ? number : null;
}

function revisionSummary(row: any): QuoteRevisionSummary {
    return {
        ...row,
        quoteRev: Number(row?.quoteRev ?? 0),
        subtotalExGst: moneyOrNull(row?.subtotalExGst),
        gstAmount: moneyOrNull(row?.gstAmount),
        totalIncGst: moneyOrNull(row?.totalIncGst),
        documentTotalIncGst: moneyOrNull(row?.documentTotalIncGst),
    };
}

export async function getQuoteJobsCapabilities(): Promise<QuoteJobsCapabilities> {
    const body: any = await apiFetch(`${BASE}/capabilities`);
    return { enabled: body?.enabled === true, killSwitch: body?.killSwitch === true };
}

function reasonEventOf(row: any): QuoteJobReasonEvent | null {
    if (!row || typeof row !== 'object' || typeof row.id !== 'string') return null;
    const rev = Number(row.quoteRev);
    return {
        id: row.id,
        kind: String(row.kind || ''),
        quoteRev: row.quoteRev === null || row.quoteRev === undefined || !Number.isFinite(rev) ? null : rev,
        reasonKind: typeof row.reasonKind === 'string' && row.reasonKind ? row.reasonKind as RevisionReasonKind : null,
        note: typeof row.note === 'string' ? row.note : null,
        siteVisitNeeded: typeof row.siteVisitNeeded === 'boolean' ? row.siteVisitNeeded : null,
        occurredAt: typeof row.occurredAt === 'string' ? row.occurredAt : null,
    };
}

function reasonDocumentsOf(rows: unknown): ReasonDocumentRef[] {
    if (!Array.isArray(rows)) return [];
    return rows
        .filter((row: any) => row && typeof row.id === 'string')
        .map((row: any) => ({ id: row.id, name: String(row.name || '') }));
}

export async function reviseQuote(projectId: string, expectedUpdatedAt: string, extras: ReviseRequestExtras = {}): Promise<ReviseResult> {
    // Only what is there is sent: with no reason the body is exactly what an
    // older tab sends, and the server treats it the same way.
    const request: Record<string, unknown> = { expectedUpdatedAt };
    if (extras.reason) {
        request.reason = extras.reason;
        if (Array.isArray(extras.attachments) && extras.attachments.length > 0) request.attachments = extras.attachments;
        if (Array.isArray(extras.otherFileNames) && extras.otherFileNames.length > 0) request.otherFileNames = extras.otherFileNames;
    }
    const body: any = await apiFetch(`${BASE}/quotes/${encodeURIComponent(projectId)}/revise`, {
        method: 'POST',
        body: JSON.stringify(request),
    });
    return {
        project: body?.project,
        job: body?.job,
        frozenRevision: body?.frozenRevision ? revisionSummary(body.frozenRevision) : null,
        revisions: Array.isArray(body?.revisions) ? body.revisions.map(revisionSummary) : [],
        reasonEvent: reasonEventOf(body?.reasonEvent),
        reasonDocuments: reasonDocumentsOf(body?.reasonDocuments),
    };
}

/**
 * apiFetch leaves the value under an opaque key untouched, and `source` is one
 * of them (data-service-api.js OPAQUE_JSONB_KEYS, for the receivables CSV
 * rows). So the split's source row arrives with its column names as Postgres
 * sends them (qm_quote_no, quote_rev, quote_data). This renames the columns the
 * way apiFetch renames target's, so both halves read alike. Values stay as
 * sent: quote_data's contents are opaque on target too.
 */
function sourceRowColumnsToCamel(row: unknown): Record<string, any> | null {
    if (!row || typeof row !== 'object' || Array.isArray(row)) return null;
    const out: Record<string, any> = {};
    for (const [key, value] of Object.entries(row)) {
        out[key.replace(/_([a-z])/g, (_match, letter: string) => letter.toUpperCase())] = value;
    }
    return out;
}

export async function splitQuote(projectId: string, request: SplitRequest): Promise<SplitResult> {
    // A split with no reason sends exactly what it sent before.
    const { reason, ...rest } = request;
    const body: any = await apiFetch(`${BASE}/quotes/${encodeURIComponent(projectId)}/split`, {
        method: 'POST',
        body: JSON.stringify(reason ? { ...rest, reason } : rest),
    });
    return {
        source: sourceRowColumnsToCamel(body?.source),
        target: body?.target,
        job: body?.job ?? null,
        skippedMedia: Array.isArray(body?.skippedMedia) ? body.skippedMedia : [],
        quoteNumberWarning: body?.quoteNumberWarning ?? null,
        replayed: body?.replayed === true,
        sourceAdvanced: body?.sourceAdvanced === true,
        reasonEvent: reasonEventOf(body?.reasonEvent),
        reasonDocuments: reasonDocumentsOf(body?.reasonDocuments),
    };
}

export async function getQuoteJob(jobId: string): Promise<QuoteJobView> {
    const body: any = await apiFetch(`${BASE}/${encodeURIComponent(jobId)}`);
    return {
        job: body?.job,
        quotes: Array.isArray(body?.quotes)
            ? body.quotes.map((quote: any) => ({ ...quote, quoteRev: Number(quote?.quoteRev ?? 0), quoteTotal: moneyOrNull(quote?.quoteTotal) }))
            : [],
        revisions: Array.isArray(body?.revisions) ? body.revisions.map(revisionSummary) : [],
    };
}

export async function listQuoteRevisions(projectId: string): Promise<QuoteRevisionSummary[]> {
    const body: any = await apiFetch(`${BASE}/quotes/${encodeURIComponent(projectId)}/revisions`);
    return Array.isArray(body?.revisions) ? body.revisions.map(revisionSummary) : [];
}

export async function getQuoteRevision(revisionId: string): Promise<QuoteRevision> {
    const row: any = await apiFetch(`${BASE}/revisions/${encodeURIComponent(revisionId)}`);
    return {
        ...revisionSummary(row),
        quoteData: row?.quoteData && typeof row.quoteData === 'object' ? row.quoteData : {},
        formData: row?.formData && typeof row.formData === 'object' ? row.formData : {},
        scope: Array.isArray(row?.scope) ? row.scope : [],
    };
}

export async function importQuote(request: ImportQuoteRequest): Promise<ImportQuoteResult> {
    const body: any = await apiFetch(`${BASE}/import`, {
        method: 'POST',
        body: JSON.stringify(request),
    });
    return {
        project: body?.project,
        job: body?.job,
        frozenRevision: body?.frozenRevision ? revisionSummary(body.frozenRevision) : null,
    };
}
